import { useContext } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { DrawingContext } from "../contexts/DrawingContext";
import * as geojson from "geojson";

import { StrategyContext } from "../contexts/StrategyContext";

import { api } from "../utils/api";
import { ApiResponse, ApiError } from "../types/api";
import {
  Treatments,
  TreatmentCostAnalysis,
} from "../types/treatment";
import {useDate} from "../contexts/DateContext";
import { convertKeysToSnakeCase } from "../utils/data";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTimezone } from "../contexts/TimezoneContext";
import { toApiDate } from "../utils/datetime";
import {
  REVEGETATION_TREATMENT,
  UNDERSTORY_THINNING_TREATMENT,
  SILVICULTURE_TREATMENT,
  CHEMICAL_RETARDANT_TREATMENT,
} from "../constants/treatmentTypes";

dayjs.extend(customParseFormat);

export const useTreatments = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedStrategy } = useContext(StrategyContext);

  const result = useQuery({
    queryKey: ["strategy", "treatments", selectedStrategy?.id],
    queryFn: () =>
      api.request<ApiResponse<Treatments>>(
        "/api/projects/" +
          projectId +
          "/mitigationStrategies/" +
          selectedStrategy?.id +
          "/treatments"
      ),
    enabled: !!selectedStrategy,
    refetchOnWindowFocus: false,
  });

  const { data: treatments, isLoading, error, refetch } = result;

  return { treatments, isLoading, error, refetch };
};

type CostAnalysisQueryParams = {
  startDate?: string | null;
  endDate?: string | null;
};

export const useTreatmentCostAnalysis = (
  params: CostAnalysisQueryParams = {},
  enabled: boolean = true
) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedStrategy } = useContext(StrategyContext);

  return useQuery({
    queryKey: [
      "strategy",
      "treatments",
      "cost-analysis",
      selectedStrategy?.id,
      params.startDate || null,
      params.endDate || null,
    ],
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.startDate) {
        searchParams.append("start_date", params.startDate);
      }
      if (params.endDate) {
        searchParams.append("end_date", params.endDate);
      }

      const queryString = searchParams.toString();
      const query = queryString ? `?${queryString}` : "";

      return api.request<ApiResponse<TreatmentCostAnalysis>>(
        `/api/projects/${projectId}/mitigationStrategies/${selectedStrategy?.id}/treatments/cost-analysis${query}`
      );
    },
    enabled: !!selectedStrategy && enabled,
    refetchOnWindowFocus: false,
  });
};

export const useAddTreatment = (projectId: string) => {
  const queryClient = useQueryClient();
  const { geojson } = useContext(DrawingContext);
  const { selectedStrategy, refreshStrategy } = useContext(StrategyContext);
  const { selectedDate } = useDate();
  const { selectedTimezone } = useTimezone();

  return useMutation<ApiResponse<any>, ApiError, any>({
    mutationFn: (treatmentData) => {
      const {
        name,
        residualFuel,
        endDate,
        type,
        vegClass,
        vegDate,
        startDate,
        width,
        costPerUnit,
        elevatedFuelReductionPct,
        surfaceFuelReductionPct,
        removeSlash,
        cbhRaiseM,
        cbdReductionPct,
        canopyHeightReductionPct,
        retardantProduct,
        coverageFraction,
        seriesId,
        uploadEndDateConfig,
        geojsonOverride,
      } = treatmentData; // Destructure data from a single object

      // Use caller-supplied geojson (with per-feature widths applied) if available
      const activeGeojson = geojsonOverride ?? geojson;

      const formattedStartDate = toApiDate(startDate, selectedTimezone);
      const formattedPayloadEndDate = toApiDate(endDate, selectedTimezone);
      const formattedVegDate = toApiDate(vegDate, selectedTimezone);

      const resolveUploadEndDate = (feature: geojson.Feature) => {
        if (!uploadEndDateConfig) {
          return undefined;
        }
        if (uploadEndDateConfig.mode === "fixed") {
          return uploadEndDateConfig.value;
        }

        const rawValue = feature?.properties?.[uploadEndDateConfig.field];
        if (rawValue === undefined || rawValue === null || rawValue === "") {
          return undefined;
        }

        if (uploadEndDateConfig.format) {
          const parsed = dayjs(
            String(rawValue),
            uploadEndDateConfig.format,
            true
          );
          if (parsed.isValid()) {
            return parsed.format("YYYY-MM-DD");
          }
        }

        return toApiDate(rawValue, selectedTimezone);
      };

      const newGeojson = {
        ...activeGeojson,
        features: activeGeojson.features.map((feature: geojson.Feature) => ({
          ...feature,
          properties: {
            Name: name,
            ResidualFuel: residualFuel,
            Type: type,
            VegClass: vegClass,
            ...(type === UNDERSTORY_THINNING_TREATMENT
              ? {
                  ElevatedFuelReductionPct: elevatedFuelReductionPct,
                  SurfaceFuelReductionPct: surfaceFuelReductionPct,
                  RemoveSlash: removeSlash,
                }
              : {}),
            ...(type === SILVICULTURE_TREATMENT
              ? {
                  ElevatedFuelReductionPct: elevatedFuelReductionPct,
                  SurfaceFuelReductionPct: surfaceFuelReductionPct,
                  RemoveSlash: removeSlash,
                  CbhRaiseM: cbhRaiseM,
                  CbdReductionPct: cbdReductionPct,
                  CanopyHeightReductionPct: canopyHeightReductionPct,
                }
              : {}),
            ...(type === CHEMICAL_RETARDANT_TREATMENT
              ? {
                  RetardantProduct: retardantProduct,
                  CoverageFraction: coverageFraction,
                }
              : {}),
            // Preserve per-feature width from uploaded file (field-based or manual applied per feature)
            ...(feature.properties?.width !== undefined && feature.properties?.width !== null ? { width: feature.properties.width } : {}),
            // Payload-level width (drawn LINE features) takes precedence
            ...(width ? { width: width } : {}),
            ...(costPerUnit !== undefined
              ? { CostPerUnit: costPerUnit }
              : {}),
            ...(seriesId ? { SeriesId: seriesId } : {}),
            ...(formattedStartDate ? { StartDate: formattedStartDate } : {}),
            ...(() => {
              const resolvedUploadDate = resolveUploadEndDate(feature);
              if (type === REVEGETATION_TREATMENT) {
                if (resolvedUploadDate) {
                  return { VegDate: resolvedUploadDate };
                }
                return formattedVegDate ? { VegDate: formattedVegDate } : {};
              }

              if (resolvedUploadDate) {
                return { EndDate: resolvedUploadDate };
              }
              return formattedPayloadEndDate ? { EndDate: formattedPayloadEndDate } : {};
            })(),
          },
        })),
      };

      return api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/mitigationStrategies/${selectedStrategy?.id}/treatments`,
        {
          method: "POST",
          body: { geojson: newGeojson }, // Send the complete GeoJSON
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["strategy", "treatments", selectedStrategy?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategy", selectedStrategy?.id, selectedDate],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategyJob"],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategyStatus"],
      });
      // A revegetation treatment can introduce a new vegetation class (the
      // backend auto-creates its VegetationType) — refetch the project's
      // veg_classes and the type list so the new class shows in dropdowns.
      queryClient.invalidateQueries({ queryKey: ["project", String(projectId)] });
      queryClient.invalidateQueries({ queryKey: ["vegetationTypes"] });
      refreshStrategy();
    },
  });
};

export const useBulkDeleteTreatments = (projectId: number) => {
  const queryClient = useQueryClient();
  const { selectedStrategy, refreshStrategy } = useContext(StrategyContext);
  const { selectedDate } = useDate();

  return useMutation<ApiResponse<any>, ApiError, number[]>({
    mutationFn: (ids: number[]) => {
      return api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/mitigationStrategies/${selectedStrategy?.id}/treatments/bulk_delete`,
        {
          method: "POST",
          body: { ids: ids },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["strategy", "treatments", selectedStrategy?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategy", selectedStrategy?.id, selectedDate],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategyJob"],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategyStatus"],
      });
      refreshStrategy();
    },
    onError: (error) => {
      console.error("Error bulk deleting treatments:", error);
    },
  });
};

type DeleteTreatmentInput = {
  id: number;
  deleteSeries?: boolean;
};

export const useDeleteTreatment = (projectId: number) => {
  const queryClient = useQueryClient();
  const { selectedStrategy, refreshStrategy } = useContext(StrategyContext);
  const { selectedDate } = useDate();

  return useMutation<ApiResponse<any>, ApiError, DeleteTreatmentInput>({
    mutationFn: ({ id, deleteSeries = false }: DeleteTreatmentInput) => {
      const deleteSeriesQuery = deleteSeries ? "?delete_series=true" : "";
      return api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/mitigationStrategies/${selectedStrategy?.id}/treatments/${id}${deleteSeriesQuery}`,
        {
          method: "DELETE",
        }
      );
    },
    onSuccess: () => {
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: ["strategy", "treatments", selectedStrategy?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategy", selectedStrategy?.id, selectedDate],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategyJob"],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategyStatus"],
      });
      refreshStrategy();
    },
    onError: (error) => {
      console.error("Error deleting treatment:", error);
    },
  });
};

export const useUpdateTreatment = (
  projectId: string,
  treatmentId: string | number
) => {
  const queryClient = useQueryClient();
  const { selectedStrategy, refreshStrategy } = useContext(StrategyContext);
  const { selectedDate } = useDate();

  return useMutation<ApiResponse<any>, ApiError, any>({
    mutationFn: (formData: any) => {
      return api.request<ApiResponse<any>>(
        `/api/projects/${projectId}/mitigationStrategies/${selectedStrategy?.id}/treatments/${treatmentId}`,
        {
          method: "PATCH",
          body: convertKeysToSnakeCase(formData),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["strategy", "treatments", selectedStrategy?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategy", selectedStrategy?.id, selectedDate],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategyJob"],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategyStatus"],
      });
      // A veg_class set via PATCH can also introduce a new VegetationType.
      queryClient.invalidateQueries({ queryKey: ["project", String(projectId)] });
      queryClient.invalidateQueries({ queryKey: ["vegetationTypes"] });
      refreshStrategy();
    },
  });
};
